import { useCallback } from "react";
import { MailRemovedNotification } from "../components/Pages/Inbox/Reusable/MailRemovedNotification.js";
import useInbox from "hooks/useInbox.ts";
import { useUser } from "../contexts/userContext/userContext.js";
import { useToasts } from "react-toast-notifications/dist/ToastProvider.js";
import { useModal } from "../contexts/modalContext/modalContext.js";
import { useContent } from "../contexts/contentContext/contentContext.js";
import Button from "../components/Elements/Button/Button.js";

const useRemoveMailFunction = ({
  activeArray,
  status,
  pendingArray = [],
  setLoading,
  filesSize,
}) => {
  const { getHtmlByKey, getTextByKey } = useContent();
  const { removeMailsAction, fetchMailsCount, getMailsData } = useInbox();
  const { getUserData, updateUserUsedStorage } = useUser();
  const { addToast } = useToasts();
  const { setModal, closeModal } = useModal();

  const mailStatus =
    status || (activeArray.length > 0 ? "active" : "pendingRemoved");

  const func = useCallback(async () => {
    await removeMailsAction({
      mailsArray: activeArray.length > 0 ? activeArray : pendingArray,
      mailStatus,
      subUsersCount: getUserData().subUsersCount,
      withSavedByOtherUser: false,
      userId: getUserData()?.UserId,
      showToast: (mailsRemovedStatus) => {
        const moveFromStorage = async () => {
          // find the mails were saved by other users
          const mailsArraywithSavedByOtherUser = activeArray.filter(
            (mailId) => getMailsData()?.[mailId]?.isSaved
          );
          await removeMailsAction({
            mailsArray: mailsArraywithSavedByOtherUser,
            mailStatus,
            withSavedByOtherUser: true,
            userId: getUserData()?.UserId,
            showToast: () =>
              addToast(getTextByKey("removed-from-user-storage"), {
                appearance: "success",
              }),
          });
          closeModal();
        };

        const MailRemovingNotAllowed = () => (
          <div className="remove-files-wrapper">
            <h1>{getTextByKey("mail-removing-not-allowed-title")}</h1>
            <p>{getHtmlByKey("mail-removing-not-allowed")}</p>
            <div className="buttons">
              <Button onClick={closeModal} blank>
                {getTextByKey("cancel")}
              </Button>
              <Button onClick={moveFromStorage}>
                {getTextByKey("move-from-my-storage")}
              </Button>
            </div>
          </div>
        );

        const MailMultipleRemovingNotAllowed = () => (
          <div className="remove-files-wrapper">
            <h1>{getTextByKey("mail-multiple-removing-not-allowed-title")}</h1>
            <p>{getHtmlByKey("mail-multiple-removing-not-allowed")}</p>
            <div className="buttons">
              <Button onClick={closeModal} blank>
                {getTextByKey("cancel")}
              </Button>
              <Button onClick={moveFromStorage}>
                {getTextByKey("move-from-my-storage")}
              </Button>
            </div>
          </div>
        );

        // single mail deleted
        if (activeArray.length === 1) {
          // represents what need to be done with the mail
          const removedStatus = mailsRemovedStatus?.[0];

          if (removedStatus === "remove") {
            return addToast(
              <MailRemovedNotification
                mailStatus={mailStatus}
                activeArray={activeArray}
                pendingRemovedArray={pendingArray}
                undoAction={removeMailsAction}
              />,
              {
                appearance: "info",
              }
            );
          } else if (removedStatus === "status change") {
            setModal({
              modal: MailRemovingNotAllowed,
              modalSize: "full-modal",
            });
            return "";
          } else if (removedStatus === "not allowed") {
            addToast(getHtmlByKey("mail-saved-by-another-user"), {
              appearance: "error",
            });
            return "";
          }
        } else {
          // multiple mails deleted
          if (mailsRemovedStatus.every((status) => status === "remove")) {
            return addToast(
              <MailRemovedNotification
                mailStatus={mailStatus}
                activeArray={activeArray}
                pendingRemovedArray={pendingArray}
                undoAction={removeMailsAction}
              />,
              {
                appearance: "info",
              }
            );
          } else if (
            mailsRemovedStatus.some((status) => status === "status change")
          ) {
            setModal({
              modal: MailMultipleRemovingNotAllowed,
              modalSize: "full-modal",
            });
            return "";
          } else if (
            mailsRemovedStatus.some((status) => status === "not allowed")
          ) {
            addToast(getHtmlByKey("mail-saved-by-another-user"), {
              appearance: "error",
            });
            return "";
          }
        }
      },
      // errNotiFunc: () => {
      // addToast(
      //   "This email is saved by other users. The removal of this email will be from your storage only, but still, be kept to access by other users",
      //   {
      //     appearance: "warning",
      //   }
      // );
      // },
      setLoadingStatus: setLoading,
    });

    setTimeout(() => {
      fetchMailsCount();
    }, 1000);

    // update used storage by user locally

    (activeArray.length > 0 ? activeArray : pendingArray).forEach((mailId) => {
      const { mailStatus, isEmbedManuallyDeleted } = getMailsData()[mailId];
      if (
        mailStatus === "saved" ||
        (isEmbedManuallyDeleted && mailStatus === "sent")
      ) {
        updateUserUsedStorage({
          UserUsedStorage: getUserData().UserUsedStorage - filesSize,
        });
      }

      if (isEmbedManuallyDeleted && mailStatus === "pendingRemoved") {
        updateUserUsedStorage({
          UserUsedStorage: getUserData().UserUsedStorage + filesSize,
        });
      }
    });
  }, [activeArray, pendingArray, mailStatus]);

  return func;
};

export default useRemoveMailFunction;
